import { Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export const CHUNK_RELOAD_GUARD_KEY = 'zeymoChunkReloadAt';

/** Don't force more than one reload per window — protects against a reload loop
 *  if a chunk is genuinely missing from the *current* build too. */
const RELOAD_GUARD_WINDOW_MS = 30_000;

/**
 * Recovers from stale-session lazy-chunk failures after a redeploy (ZEY-789).
 *
 * A prod deploy rotates the hashed lazy-chunk filenames; a tab opened before the
 * deploy still references the old names, so the first navigation to a
 * not-yet-visited lazy route 404s → ChunkLoadError → the router silently aborts
 * the navigation and the click appears dead. This service listens for those
 * failures and forces a full page load of the *target* URL, which fetches the
 * new index.html + chunks and lands the user exactly where they clicked.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  constructor(private router: Router) {}

  init(): void {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe(event => {
        if (!isChunkLoadError(event.error) || !this.tryAcquireReloadGuard()) {
          return;
        }
        this.reloadTo(event.url);
      });
  }

  /** Full page load — separate method so specs can observe it (location.assign is unstubbable). */
  reloadTo(url: string): void {
    window.location.assign(url);
  }

  private tryAcquireReloadGuard(): boolean {
    try {
      const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) ?? 0);
      if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
        return false;
      }
      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
      return true;
    } catch {
      // sessionStorage unavailable (private mode edge cases) — still recover, just unguarded.
      return true;
    }
  }
}

/** Matches esbuild/native dynamic-import failures (current builder) and webpack's ChunkLoadError. */
function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? '');
  return /ChunkLoadError|Loading chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(text);
}
